// commitTrip — bulk-flush a Shop Mode trip to pantry_items + receipts +
// learning writes. Pure orchestrator: takes the resolved scans plus the
// async write functions (so this file has no React/Supabase context
// directly) and runs everything in one Promise.all.
//
// The unified write surface mirrors additem/store.js's commit() — same
// rememberCorrection key shapes, same buildPantryRow projection, same
// fire-and-forget posture for non-load-bearing writes.

import { findIngredient, BRAND_ALIASES } from "../../data/ingredients";
import { rememberBarcodeCorrection } from "../../lib/barcodeCorrections";
import { segmentScanText } from "../../lib/segmentScanText";
import { tilesForIngredient } from "../../lib/tileMatching";
import { parsePackageSize } from "../../lib/canonicalResolver";

// Walk BRAND_ALIASES on a piece of raw text — line.rawText or scan
// displayName — and return the first matching display brand. Used by
// the matcher when a side's brand field is unset but the rawText
// contains an abbreviation Sonnet missed (e.g. "SWN" → "Swanson").
function brandFromText(text) {
  if (!text) return null;
  const t = String(text);
  for (const { pattern, brand } of BRAND_ALIASES) {
    if (pattern.test(t)) return brand;
  }
  return null;
}

function defaultLocationForCategoryLocal(category) {
  if (!category) return null;
  if (["dairy", "meat", "seafood", "produce"].includes(category)) return "fridge";
  return "pantry";
}

// Translate a trip_scans row (camelCase from useShopTrip) into the
// pantry_items insert shape. Mirrors additem/store.js buildPantryRow
// but draws from the in-trip row directly since Shop Mode doesn't
// keep a per-row draft store outside the Resolve phase.
function pantryRowFromScan(scan, override = {}) {
  const ing = scan.canonicalId ? findIngredient(scan.canonicalId) : null;
  // Override may be either a partial autofill (camelCase keys) or the
  // full pantry-row payload returned by the wizard's buildPantryRow()
  // (snake_case). Read both shapes for each axis.
  //
  // Package size cascade — must mirror classifyScan's hasSize check
  // because GREEN-tier requires a size. If the user manually entered
  // a size in ResolveScreen → override.{max,unit}. If the scan auto-
  // resolved to green from OFF data → off_payload.quantity has it.
  // We have to check BOTH; reading only override misses the auto-
  // resolved case (where the user never touched the row) and lands
  // pantry rows with null size despite GREEN status.
  const offPkg = scan.offPayload?.quantity
    ? parsePackageSize(scan.offPayload.quantity)
    : null;
  const max =
    override.max ??
    override.packageAmount ??
    offPkg?.amount ??
    null;
  const unit =
    override.unit ||
    override.packageUnit ||
    offPkg?.unit ||
    null;
  const location =
    override.location ||
    ing?.storage?.location ||
    defaultLocationForCategoryLocal(ing?.category) ||
    null;
  const candidateTiles = scan.canonicalId ? tilesForIngredient(scan.canonicalId) : [];
  const tile_id =
    override.tile_id ||
    override.tileId ||
    candidateTiles[0] ||
    null;
  // pantry_items.name is NOT NULL with no default. Red-tier scans
  // (no canonical, no productName, no displayName) would null-out
  // the entire bulk insert if we ever returned null here. Fall back
  // to a UPC-derived placeholder so even unbound scans land — the
  // "Scans Never Fail" rule. User can rename later from the kitchen.
  //
  // Order per the two-phase rule: canonical display name pins first
  // when bound; scan.displayName next (already canonical-or-cleaned
  // from classifyScan); raw productName only for legacy rows, and
  // even then through the segmenter so marketing copy can't mint a
  // kitchen row name.
  const upcTail = scan.barcodeUpc ? scan.barcodeUpc.slice(-6) : "";
  const name = ing?.name ||
               scan.displayName ||
               (scan.productName
                 ? segmentScanText(scan.productName, {
                     brand: scan.brand || null,
                     canonicalId: scan.canonicalId || null,
                   }).name
                 : null) ||
               override.name ||
               (upcTail ? `Unidentified · ${upcTail}` : "Unidentified item");
  // Every row gets the SAME key set with non-null values for the
  // NOT-NULL-with-default columns. supabase-js's bulk .insert()
  // normalizes the column set to the union of every row's keys,
  // then back-fills missing keys with null on rows that omitted
  // them. Mixed-schema arrays therefore hit NOT-NULL violations
  // even when the DB has a default — defaults only fire when the
  // key is absent from the INSERT entirely, and bulk insert makes
  // that impossible the moment ONE row sets the column. Defaults
  // here mirror the DB column defaults (migration 0004 + later):
  //   amount   → 0
  //   max      → 1
  //   unit     → ''
  //   category → 'pantry'
  // Nullable columns can stay nullable (state, cut, type_id, etc.)
  // because supabase-js fills them with null and Postgres accepts.
  // Variant axis — flavor / product line under brand. Read from
  // override (set when the trip-resolve wizard ran a variant step) or
  // fall back to scan.variant carried on the trip_scans row (set
  // earlier by ScanPipeline's segmenter / Haiku / correction). Empty
  // string treated as null so an explicit "no variant" pick is
  // preserved without writing "" to the JSONB column.
  const variant = (() => {
    const fromOverride = override.variant ?? null;
    if (typeof fromOverride === "string") return fromOverride.trim() || null;
    if (fromOverride === null && Object.prototype.hasOwnProperty.call(override, "variant")) return null;
    const fromScan = scan.variant;
    return typeof fromScan === "string" ? (fromScan.trim() || null) : null;
  })();

  // attributes JSONB — variant lives here alongside any other override
  // attributes the wizard wrote (e.g. claims, nutrition flags). Merge
  // rather than overwrite so wizard-supplied attributes survive the
  // variant injection.
  const baseAttributes = (override.attributes && typeof override.attributes === "object")
    ? { ...override.attributes }
    : null;
  const mergedAttributes = variant
    ? { ...(baseAttributes || {}), variant }
    : baseAttributes;

  // Claims hygiene — drop any claim token that matches the variant so
  // "Sharp" doesn't land on both attributes.variant and claims[].
  // Same precedence rule ScanPipeline applies at scan time.
  const rawClaims = Array.isArray(scan.claims) ? scan.claims : [];
  const claims = variant
    ? rawClaims.filter((c) =>
        String(c || "").trim().toLowerCase() !== String(variant).trim().toLowerCase()
      )
    : rawClaims;

  const row = {
    name,
    brand: scan.brand?.trim() || null,
    canonical_id: scan.canonicalId || null,
    barcode_upc: scan.barcodeUpc || null,
    location,
    tile_id,
    state: override.state || null,
    cut: override.cut || null,
    type_id: override.type_id || override.typeId || null,
    category: override.category || ing?.category || "pantry",
    // amount is the CURRENT remaining quantity. A just-bought item is
    // full, so amount === max. If max is genuinely null after the
    // override + off_payload cascade above, that scan shouldn't have
    // been GREEN in the first place (GREEN requires canonical + brand
    // + size — see classifyScan.js:171). Falling back to a
    // synthetic 1/1 papers over the upstream bug; instead, land a
    // sentinel max=1 so the row is visible in the kitchen and the
    // phantom-green-audit log is the source-of-truth diagnostic. Do
    // NOT loosen the GREEN tier rule to compensate for this fallback.
    amount: max ?? 1,
    max:    max ?? 1,
    unit:   unit || "",
    expires_at: override.expires_at || override.expiresAt || null,
    nutrition_override: override.nutrition_override || override.nutritionOverride || null,
    attributes: mergedAttributes,
    // Variants / claims — modifier tokens like "spicy", "sharp",
    // "smoked", "low sodium". Pulled from scan.claims so anything
    // the user typed in the trip-list inline editor survives into
    // the kitchen and stays searchable.
    claims,
  };
  return row;
}

// Fire the rememberCorrection write for a scan that has at least a
// canonical bound. Pulls placement/size/state/cut from the per-scan
// override (set via the wizard or auto-fill) so future re-scans of
// the same UPC pre-fill EVERY axis the user supplied — not just
// canonical and brand. Non-blocking; errors logged.
async function rememberFromScan(scan, { userId, isAdmin, override = {} }) {
  if (!scan.canonicalId || !scan.barcodeUpc) return;
  // Override may carry either snake_case (full pantryRow from the
  // wizard) or camelCase (autofill writes). Read both for each axis.
  // Size also falls back to off_payload.quantity — same cascade as
  // pantryRowFromScan, otherwise the correction write loses size on
  // auto-resolved greens and future re-scans don't pre-fill.
  const tileId   = override.tile_id   || override.tileId   || null;
  const location = override.location  || null;
  const state    = override.state     || null;
  const cut      = override.cut       || null;
  const typeId   = override.type_id   || override.typeId   || null;
  // Variant — same cascade as pantryRowFromScan. override.variant
  // wins (user explicitly picked in resolve wizard), else scan.variant
  // (set by ScanPipeline's segmenter / Haiku / correction tier).
  const variant = (() => {
    const fromOverride = override.variant ?? null;
    if (typeof fromOverride === "string") return fromOverride.trim() || null;
    if (fromOverride === null && Object.prototype.hasOwnProperty.call(override, "variant")) return null;
    const fromScan = scan.variant;
    return typeof fromScan === "string" ? (fromScan.trim() || null) : null;
  })();
  const offPkg   = scan.offPayload?.quantity
    ? parsePackageSize(scan.offPayload.quantity)
    : null;
  const sizeAmt  = override.max ?? override.packageAmount ?? offPkg?.amount ?? null;
  const sizeUnit = override.unit || override.packageUnit || offPkg?.unit || null;
  try {
    await rememberBarcodeCorrection({
      userId,
      isAdmin,
      barcodeUpc:        scan.barcodeUpc,
      canonicalId:       scan.canonicalId,
      brand:             scan.brand || null,
      variant,
      tileId,
      location,
      state,
      cut,
      typeId,
      packageSizeAmount: sizeAmt,
      packageSizeUnit:   sizeUnit,
      name:              scan.displayName || null,
    });
  } catch (e) {
    console.warn("[commit-trip] rememberCorrection failed:", e?.message || e);
  }
}

// Public: orchestrate the trip commit. Returns { ok, errors,
// pantryRowIds, receiptId, summary }.
//
//   scans         — camelCase trip_scans rows from useShopTrip
//   priceByScanId — Map<scanId, { priceCents, lineIndex }>
//   receiptMeta   — { store, date, totalCents } | null
//   imageData     — { base64, mediaType } | null (optional receipt photo)
//   userId, isAdmin
//   supabase      — supabase-js client (passed in to keep this file React-free)
//   tripId        — shopping_trips.id
export async function commitTrip({
  scans,
  priceByScanId,
  placementOverrides,   // Map<scanId, {location, tileId, state, cut, packageAmount, packageUnit}>
  receiptMeta,
  imageData,
  userId,
  isAdmin,
  supabase,
  tripId,
  // The store picked at trip START. Receipt OCR wins when present
  // (fresher, verbatim from the paper) but a receipt-less commit
  // must not null out the user's pick.
  tripStoreName = null,
}) {
  const errors = [];
  let receiptId = null;

  // 1. Receipt row (if photo + meta present).
  if (imageData && receiptMeta) {
    try {
      const scanItemsSnapshot = scans.map((s) => ({
        name:        s.displayName || s.productName || s.brand || s.barcodeUpc,
        rawText:     s.productName,
        brand:       s.brand,
        barcode:     s.barcodeUpc,
        canonicalId: s.canonicalId,
        priceCents:  priceByScanId?.get?.(s.id)?.priceCents ?? null,
      }));
      const { data, error } = await supabase
        .from("receipts")
        .insert({
          user_id:      userId,
          store_name:   receiptMeta.store || null,
          receipt_date: /^\d{4}-\d{2}-\d{2}$/.test(String(receiptMeta.date || ""))
            ? receiptMeta.date
            : null,
          total_cents:  typeof receiptMeta.totalCents === "number"
            ? receiptMeta.totalCents
            : null,
          item_count:   scans.length,
          scan_items:   scanItemsSnapshot,
        })
        .select("id")
        .single();
      if (error) throw error;
      receiptId = data?.id || null;

      // Upload image, best-effort.
      if (receiptId && imageData.base64) {
        try {
          const ext = (imageData.mediaType || "image/jpeg").split("/")[1]?.split(";")[0] || "jpg";
          const path = `${userId}/${receiptId}.${ext}`;
          const bin = atob(imageData.base64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: imageData.mediaType });
          const { error: upErr } = await supabase.storage
            .from("scans")
            .upload(path, blob, { contentType: imageData.mediaType, upsert: true });
          if (!upErr) {
            await supabase.from("receipts").update({ image_path: path }).eq("id", receiptId);
          }
        } catch (e) {
          console.warn("[commit-trip] receipt image upload failed:", e);
        }
      }
    } catch (e) {
      errors.push({ op: "receipt", error: e?.message || String(e) });
    }
  }

  // 2. Pre-flight phantom-green audit. Surface (don't block) any scan
  // that's marked status:"green" but is actually missing one of the
  // required green axes (canonical, brand, package size). Lets the
  // caller decide whether to abort or proceed; we just log loudly so
  // the data drift is observable in the console + telemetry.
  const phantomGreens = scans.filter((s) => {
    if (s.status !== "green") return false;
    const override = placementOverrides?.get?.(s.id) || {};
    const hasCanonical = !!s.canonicalId;
    const hasBrand = !!(s.brand && String(s.brand).trim());
    const sizeAmt = override.packageAmount ?? override.max ?? null;
    const sizeUnit = override.packageUnit ?? override.unit ?? null;
    const hasSize = !!(sizeAmt && sizeUnit);
    return !(hasCanonical && hasBrand && hasSize);
  });
  if (phantomGreens.length > 0) {
    console.warn(
      `[commit-trip] ${phantomGreens.length} phantom-green row(s) — status:"green" but missing brand or size. Committing anyway with null fields:`,
      phantomGreens.map((s) => ({
        id: s.id,
        canonicalId: s.canonicalId,
        brand: s.brand,
        productName: s.productName,
      })),
    );
    // Surface to errors so the caller's UI can flag these to the user.
    errors.push({
      op: "phantom-green-audit",
      error: `${phantomGreens.length} row(s) marked resolved but missing brand or size`,
      scans: phantomGreens.map((s) => s.id),
    });
  }

  // 3. Bulk pantry_items insert. One round-trip for all rows.
  const pantryPayload = scans.map((s) => {
    const override = placementOverrides?.get?.(s.id) || {};
    const row = pantryRowFromScan(s, override);
    const priceCents = priceByScanId?.get?.(s.id)?.priceCents ?? null;
    return {
      ...row,
      user_id: userId,
      // qty replicates here so the pantry row reflects "how many of
      // this thing I just bought." pantry_items doesn't have a qty
      // column today, so we splay into N rows for qty>1 instead.
      _qty: s.qty || 1,
      _price: priceCents,
      _scanId: s.id,
    };
  });

  // Splay qty>1 scans into N row inserts so each pantry_items row is a
  // single distinct unit (matches pantry semantics — one row per
  // physical item in the household).
  const flatPayload = [];
  for (const row of pantryPayload) {
    const qty = Math.max(1, Math.min(99, row._qty || 1));
    for (let i = 0; i < qty; i++) {
      const { _qty, _price, _scanId, ...clean } = row;
      flatPayload.push({ ...clean });
    }
  }

  let pantryRowIds = [];
  if (flatPayload.length > 0) {
    try {
      const { data, error } = await supabase
        .from("pantry_items")
        .insert(flatPayload)
        .select("id");
      if (error) throw error;
      pantryRowIds = (data || []).map((r) => r.id);
    } catch (e) {
      // Postgres rolls back the entire bulk insert on a single row
      // failure. Log the first row and the error code so the cause
      // is observable when this happens (NOT NULL violations,
      // unknown column, RLS denial all surface here).
      console.error(
        "[commit-trip] pantry insert FAILED — 0 rows landed.",
        "code:", e?.code,
        "message:", e?.message,
        "details:", e?.details,
        "hint:", e?.hint,
        "first-row sample:", flatPayload[0],
      );
      errors.push({ op: "pantry", error: e?.message || String(e) });
    }
  }

  // 3. Fire-and-forget learning writes (per CLAUDE.md self-teaching
  //    identity rule). Each scan with a canonical contributes a
  //    correction row carrying every axis the user supplied — so
  //    re-scanning the same UPC next trip pre-fills tile, location,
  //    state, cut, and package size, not just canonical+brand.
  await Promise.all(
    scans.map((s) => {
      const override = placementOverrides?.get?.(s.id) || {};
      return rememberFromScan(s, { userId, isAdmin, override });
    }),
  );

  // 4. Mark paired shopping_list_items as purchased so the shopping
  //    list view stops showing them as outstanding. Fire-and-forget;
  //    failures are logged but never block trip close.
  const purchasedAt = new Date().toISOString();
  const pairedListIds = scans
    .map((s) => s.pairedShoppingListItemId)
    .filter(Boolean);
  if (pairedListIds.length > 0) {
    try {
      await supabase
        .from("shopping_list_items")
        .update({
          purchased_at: purchasedAt,
          purchased_trip_id: tripId,
        })
        .in("id", pairedListIds);
    } catch (e) {
      console.warn("[commit-trip] purchased-mark failed:", e?.message || e);
    }
  }

  // 5. Mark the trip checked_out + attach receipt.
  try {
    await supabase
      .from("shopping_trips")
      .update({
        status:     "checked_out",
        ended_at:   new Date().toISOString(),
        receipt_id: receiptId || null,
        store_name: receiptMeta?.store || tripStoreName || null,
      })
      .eq("id", tripId);
  } catch (e) {
    errors.push({ op: "trip-close", error: e?.message || String(e) });
  }

  return {
    ok: errors.length === 0,
    errors,
    pantryRowIds,
    receiptId,
  };
}

// Tiered matcher for receipt lines → scans. The receipt OCR (Sonnet
// in `scan-receipt`) emits `barcode`, `canonicalId`, and `brand` per
// line — pairing should USE those, not fall back to fuzzy text overlap
// on heavily-abbreviated `rawText`. Cascade:
//
//   1. UPC === UPC          (instant high-confidence)
//   2. canonical + brand    (high-confidence when both sides resolved)
//   3. canonical alone      (ok-confidence)
//   4. ≥3-char token overlap on rawText (last-resort fallback)
//
// One scan claims at most one line; one line is claimed by at most
// one scan. Each tier runs greedily over remaining (scan, line) pairs.
//
// Returns Map<scanId, { lineIndex, priceCents, tier, lineText }>.
//   tier ∈ "upc" | "canonical+brand" | "canonical" | "fuzzy"
export function matchReceiptLines(scans, receiptLines) {
  const result = new Map();
  if (!Array.isArray(receiptLines) || receiptLines.length === 0) return result;

  const claimed = new Set();   // line indices already paired

  // Strip non-digits + leading zeros. Two normalized UPCs are
  // "equivalent" if either:
  //   a) they're equal after leading-zero strip, OR
  //   b) one is a prefix of the other and lengths differ by exactly
  //      1 — handles Walmart's shifted form (drop check digit,
  //      prepend "0"). Original `031142523850` becomes Walmart form
  //      `003114252385`; after leading-zero strip the scan side is
  //      `31142523850` (11 digits) and the Walmart side is
  //      `3114252385` (10 digits). The 10-digit form is the prefix
  //      of the 11-digit form.
  const normalizeUpc = (s) => {
    if (!s) return null;
    const digits = String(s).replace(/\D/g, "").replace(/^0+/, "");
    return digits || null;
  };
  const upcEquivalent = (a, b) => {
    if (!a || !b) return false;
    if (a === b) return true;
    if (a.length === b.length + 1 && a.startsWith(b)) return true;
    if (b.length === a.length + 1 && b.startsWith(a)) return true;
    return false;
  };
  const normalizeBrand = (s) =>
    String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const tokenize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, " ")
      .split(/\s+/)
      .filter((t) => t.length >= 3);

  const claim = (scanId, lineIndex, tier) => {
    const line = receiptLines[lineIndex];
    result.set(scanId, {
      lineIndex,
      priceCents: typeof line.priceCents === "number" ? line.priceCents : null,
      tier,
      lineText: line.rawText || null,
    });
    claimed.add(lineIndex);
  };

  // Tier 1: UPC equality. Two passes per (scan, line):
  //
  //   a) The structured `barcode` field, after digit-only normalization
  //      (handles EAN-13 leading zero vs UPC-A, Walmart-shifted forms).
  //   b) Fallback: scan UPC digits appear ANYWHERE in `rawText`. This
  //      catches the common case where Sonnet read the UPC visually
  //      but didn't extract it into the structured field — the digits
  //      are still in the OCR'd line text. Per the user's feedback:
  //      "our upc scans are legit number by number the exact same as
  //      the ones on the receipt. this should be a slam dunk easy
  //      layup" — when the digits ARE there, we MUST find them.
  //
  // 8-digit minimum on the rawText fallback to avoid spurious matches
  // (a 4-digit UPC suffix could substring-match a price or quantity).
  for (const scan of scans) {
    if (result.has(scan.id)) continue;
    const scanUpc = normalizeUpc(scan.barcodeUpc);
    if (!scanUpc) continue;
    for (let i = 0; i < receiptLines.length; i++) {
      if (claimed.has(i)) continue;
      const line = receiptLines[i];

      const lineUpc = normalizeUpc(line.barcode);
      if (upcEquivalent(lineUpc, scanUpc)) {
        claim(scan.id, i, "upc");
        break;
      }

      if (scanUpc.length >= 8) {
        const rawDigits = String(line.rawText || "").replace(/\D/g, "");
        if (rawDigits.includes(scanUpc)) {
          claim(scan.id, i, "upc");
          break;
        }
        // Walmart-shifted form: scan minus check digit (last digit).
        const shifted = scanUpc.slice(0, -1);
        if (shifted.length >= 8 && rawDigits.includes(shifted)) {
          claim(scan.id, i, "upc");
          break;
        }
      }
    }
  }

  // Tier 2: same canonical + same brand (both normalized). Brand on
  // each side falls back to a BRAND_ALIASES sweep on the rawText/
  // displayName when the field itself is null — covers the common case
  // where Sonnet didn't expand "SWN" → "Swanson" but the alias map can.
  const resolveScanBrand = (scan) =>
    scan.brand ||
    brandFromText(scan.displayName) ||
    brandFromText(scan.productName) ||
    null;
  const resolveLineBrand = (line) =>
    line.brand ||
    brandFromText(line.rawText) ||
    null;

  for (const scan of scans) {
    if (result.has(scan.id)) continue;
    if (!scan.canonicalId) continue;
    const scanBrand = normalizeBrand(resolveScanBrand(scan));
    if (!scanBrand) continue;
    for (let i = 0; i < receiptLines.length; i++) {
      if (claimed.has(i)) continue;
      const line = receiptLines[i];
      if (line.canonicalId !== scan.canonicalId) continue;
      const lineBrand = normalizeBrand(resolveLineBrand(line));
      if (lineBrand && lineBrand === scanBrand) {
        claim(scan.id, i, "canonical+brand");
        break;
      }
    }
  }

  // Tier 3: same canonical, brand missing or different.
  for (const scan of scans) {
    if (result.has(scan.id)) continue;
    if (!scan.canonicalId) continue;
    for (let i = 0; i < receiptLines.length; i++) {
      if (claimed.has(i)) continue;
      if (receiptLines[i].canonicalId !== scan.canonicalId) continue;
      claim(scan.id, i, "canonical");
      break;
    }
  }

  // Tier 4: combined-signal scoring. Each (scan, line) pair gets
  // points for partial UPC overlap, brand-alias match, canonical
  // match, and token overlap. Pairs scoring ≥ 3 are eligible to claim.
  // The best-scoring eligible pair per scan wins.
  //
  // Why combined-signal: the strict tiers above all require ONE axis
  // to fully match. In the wild, OCR noise means partial signals
  // across multiple axes are often clearer than any single axis. A
  // line with a 6-digit UPC overlap + matching brand + 1 token is
  // way more confident than 5 token overlaps alone.
  //
  // Scoring:
  //   +4  partial UPC overlap (≥8 consecutive digits)
  //   +3  partial UPC overlap (≥6 consecutive digits, < 8)
  //   +2  same canonical_id
  //   +2  same brand (with BRAND_ALIASES expansion on both sides)
  //   +N  N tokens of length ≥ 3 in common
  const scored = [];
  for (const scan of scans) {
    if (result.has(scan.id)) continue;
    const scanUpc = normalizeUpc(scan.barcodeUpc);
    const scanBrand = normalizeBrand(resolveScanBrand(scan));
    const scanTokens = new Set(tokenize(scan.displayName || scan.productName));
    for (let i = 0; i < receiptLines.length; i++) {
      if (claimed.has(i)) continue;
      const line = receiptLines[i];
      let score = 0;

      // Partial UPC overlap on rawText digits — strongest signal in
      // this tier because the digits are objective even if Sonnet
      // missed extracting them into the structured `barcode` field.
      if (scanUpc) {
        const rawDigits = String(line.rawText || "").replace(/\D/g, "");
        const tail8 = scanUpc.slice(-8);
        const tail6 = scanUpc.slice(-6);
        if (tail8.length === 8 && rawDigits.includes(tail8)) score += 4;
        else if (tail6.length === 6 && rawDigits.includes(tail6)) score += 3;
      }

      // Brand match using alias expansion on both sides.
      const lineBrand = normalizeBrand(resolveLineBrand(line));
      if (scanBrand && lineBrand && scanBrand === lineBrand) score += 2;

      // Canonical match.
      if (scan.canonicalId && line.canonicalId === scan.canonicalId) score += 2;

      // Token overlap on rawText.
      if (scanTokens.size > 0) {
        const lineTokens = new Set(tokenize(line.rawText));
        for (const t of scanTokens) if (lineTokens.has(t)) score += 1;
      }

      if (score >= 3) {
        scored.push({ scanId: scan.id, lineIndex: i, score });
      }
    }
  }
  scored.sort((a, b) => b.score - a.score);
  for (const pair of scored) {
    if (result.has(pair.scanId)) continue;
    if (claimed.has(pair.lineIndex)) continue;
    claim(pair.scanId, pair.lineIndex, "fuzzy");
  }

  return result;
}
